import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Radio, Loader2, CheckCircle2, ArrowRight, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { ASSETS } from "@/lib/assets";

const FEATURES = [
  "Sunday News & communications requests in one place",
  "Easy step-by-step submissions that ask only what you need",
  "Track every request from submitted to published",
  "Efficient project management and chapter communications",
];

export default function Login() {
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "", committee: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const endpoint = mode === "login" ? "/auth/login" : "/auth/register";
      const payload = mode === "login"
        ? { email: form.email, password: form.password }
        : { name: form.name, email: form.email, password: form.password, committee: form.committee };
      const { data } = await api.post(endpoint, payload);
      login(data.user, data.token);
      navigate(data.user.role === "member" ? "/dashboard" : "/admin");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = async () => {
    if (!form.email) { setError("Enter your email above, then click Forgot password."); return; }
    try { await api.post("/auth/forgot-password", { email: form.email }); } catch (e) { /* enumeration-safe */ }
    toast.success("If an account exists, a password reset link has been emailed.");
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="flex min-h-screen">
      {/* Left brand panel */}
      <div className="relative hidden w-1/2 flex-col justify-between overflow-hidden bg-primary p-12 text-primary-foreground lg:flex">
        <img src={ASSETS.hero} alt="Chapter members collaborating"
          className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-primary via-primary/85 to-primary/40" />
        <img src={ASSETS.goldLines} alt="" aria-hidden className="absolute inset-0 h-full w-full object-cover opacity-50" />
        <img src={ASSETS.ivy} alt="" aria-hidden className="pointer-events-none absolute -top-4 -right-4 w-52 opacity-80" />
        <div className="relative z-10 flex items-center gap-2">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-foreground/15 backdrop-blur">
            <Radio className="h-6 w-6" />
          </div>
          <span className="font-heading text-2xl font-bold tracking-tight">CommConnect</span>
        </div>
        <div className="relative z-10">
          <img src={ASSETS.pearls} alt="" aria-hidden className="mb-4 h-12 w-auto" />
          <h1 className="font-heading text-4xl font-bold leading-tight tracking-tight sm:text-5xl">
            One portal.<br />Every request.<br />Clearer communication.
          </h1>
          <ul className="mt-8 space-y-3">
            {FEATURES.map((f) => (
              <li key={f} className="flex items-center gap-3 text-primary-foreground/90">
                <CheckCircle2 className="h-5 w-5 shrink-0 text-accent" /> <span>{f}</span>
              </li>
            ))}
          </ul>
        </div>
        <p className="relative z-10 text-sm text-primary-foreground/70">Designed for Alpha Kappa Alpha Sorority, Inc.® Alpha Epsilon Gamma Omega Chapter · © CMD+DesignLab 2026</p>
      </div>

      {/* Right form panel */}
      <div className="relative flex w-full flex-col items-center justify-center overflow-hidden bg-background px-6 py-12 lg:w-1/2">
        <img src={ASSETS.wave} alt="" aria-hidden className="pointer-events-none absolute inset-x-0 bottom-0 h-48 w-full object-cover object-bottom opacity-95" />
        <div className="relative z-10 w-full max-w-sm">
          <div className="mb-8 flex items-center gap-2 lg:hidden">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-primary-foreground">
              <Radio className="h-5 w-5" />
            </div>
            <span className="font-heading text-xl font-bold">CommConnect</span>
          </div>
          <h2 className="font-heading text-3xl font-bold tracking-tight">
            {mode === "login" ? "Welcome back" : "Create your account"}
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {mode === "login" ? "Sign in to manage your communications." : "Join your organization's portal."}
          </p>

          <Button variant="outline" onClick={googleLogin} data-testid="google-login-btn"
            className="mt-6 w-full gap-2 rounded-full">
            <svg className="h-4 w-4" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
            Continue with Google
          </Button>

          <div className="my-5 flex items-center gap-3">
            <div className="h-px flex-1 bg-border" />
            <span className="text-xs uppercase tracking-widest text-muted-foreground">or</span>
            <div className="h-px flex-1 bg-border" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div>
                <Label htmlFor="name">Full name</Label>
                <Input id="name" data-testid="register-name-input" value={form.name} required
                  onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" />
              </div>
            )}
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" data-testid="login-email-input" value={form.email} required
                onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" placeholder="you@organization.org" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <div className="relative mt-1.5">
                <Input id="password" type={showPassword ? "text" : "password"} data-testid="login-password-input" value={form.password} required
                  onChange={(e) => setForm({ ...form, password: e.target.value })} className="pr-10" />
                <button type="button" data-testid="toggle-password-visibility" onClick={() => setShowPassword((s) => !s)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground transition-colors hover:text-foreground">
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {mode === "login" && (
                <div className="mt-1.5 text-right">
                  <button type="button" data-testid="forgot-password-link" onClick={handleForgot}
                    className="text-xs text-primary hover:underline">Forgot password?</button>
                </div>
              )}
            </div>
            {mode === "register" && (
              <div>
                <Label htmlFor="committee">Committee (optional)</Label>
                <Input id="committee" data-testid="register-committee-input" value={form.committee}
                  onChange={(e) => setForm({ ...form, committee: e.target.value })} className="mt-1.5" />
              </div>
            )}
            {error && <p className="text-sm text-destructive" data-testid="auth-error">{error}</p>}
            <Button type="submit" disabled={loading} data-testid="auth-submit-btn" className="w-full rounded-full">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (
                <>{mode === "login" ? "Sign in" : "Create account"} <ArrowRight className="ml-1.5 h-4 w-4" /></>
              )}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-muted-foreground">
            {mode === "login" ? "New to CommConnect?" : "Already have an account?"}{" "}
            <button data-testid="toggle-auth-mode" onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
              className="font-semibold text-primary hover:underline">
              {mode === "login" ? "Create an account" : "Sign in"}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
